'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
}

interface ToastContextType {
  success: (title: string, description?: string) => string;
  error: (title: string, description?: string) => string;
  warning: (title: string, description?: string) => string;
  info: (title: string, description?: string) => string;
  loading: (title: string, description?: string) => string;
  dismiss: (id: string) => void;
  promise: <T>(
    promise: Promise<T>,
    messages: { loading: string; success: string; error: string }
  ) => Promise<T>;
}

const ToastContext = createContext<ToastContextType>({} as ToastContextType);

export const useToast = () => useContext(ToastContext);

const ICONS: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
  loading: '⟳',
};

const COLORS: Record<ToastType, { bg: string; border: string; icon: string; bar: string }> = {
  success: { bg: 'bg-zinc-900', border: 'border-green-500/40', icon: 'bg-green-500/20 text-green-400', bar: 'bg-green-500' },
  error: { bg: 'bg-zinc-900', border: 'border-red-500/40', icon: 'bg-red-500/20 text-red-400', bar: 'bg-red-500' },
  warning: { bg: 'bg-zinc-900', border: 'border-amber-500/40', icon: 'bg-amber-500/20 text-amber-400', bar: 'bg-amber-500' },
  info: { bg: 'bg-zinc-900', border: 'border-cyan-500/40', icon: 'bg-cyan-500/20 text-cyan-400', bar: 'bg-cyan-500' },
  loading: { bg: 'bg-zinc-900', border: 'border-purple-500/40', icon: 'bg-purple-500/20 text-purple-400', bar: 'bg-purple-500' },
};

const DURATIONS: Record<ToastType, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 8000,
  loading: 0,
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const colors = COLORS[toast.type];
  const duration = DURATIONS[toast.type];
  const startRef = useRef<number>(Date.now());
  const rafRef = useRef<number>(0);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    if (duration > 0) {
      startRef.current = Date.now();
      const tick = () => {
        const elapsed = Date.now() - startRef.current;
        const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
        setProgress(remaining);
        if (remaining > 0) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          handleDismiss();
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    }
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(() => onDismiss(toast.id), 300);
  };

  return (
    <div
      className={`relative flex items-start gap-3 w-full sm:w-80 p-3 sm:p-4 rounded-xl border shadow-2xl shadow-black/50 transition-all duration-300 ${colors.bg} ${colors.border} ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      }`}
    >
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-bold ${colors.icon} ${toast.type === 'loading' ? 'animate-spin' : ''}`}>
        {ICONS[toast.type]}
      </div>
      <div className="flex-1 min-w-0 pr-2">
        <p className="text-sm font-semibold text-foreground leading-tight">{toast.title}</p>
        {toast.description && (
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{toast.description}</p>
        )}
      </div>
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors text-xs mt-0.5"
      >
        ✕
      </button>
      {duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-b-xl overflow-hidden bg-white/5">
          <div
            className={`h-full transition-none ${colors.bar}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((type: ToastType, title: string, description?: string): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts((prev) => {
      const next = [{ id, type, title, description }, ...prev];
      return next.slice(0, 5); // max 5 toasts
    });
    return id;
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const success = useCallback((title: string, description?: string) => addToast('success', title, description), [addToast]);
  const error = useCallback((title: string, description?: string) => addToast('error', title, description), [addToast]);
  const warning = useCallback((title: string, description?: string) => addToast('warning', title, description), [addToast]);
  const info = useCallback((title: string, description?: string) => addToast('info', title, description), [addToast]);
  const loading = useCallback((title: string, description?: string) => addToast('loading', title, description), [addToast]);

  const promise = useCallback(async <T,>(
    p: Promise<T>,
    messages: { loading: string; success: string; error: string }
  ): Promise<T> => {
    const id = addToast('loading', messages.loading);
    try {
      const result = await p;
      dismiss(id);
      addToast('success', messages.success);
      return result;
    } catch (err) {
      dismiss(id);
      addToast('error', messages.error);
      throw err;
    }
  }, [addToast, dismiss]);

  return (
    <ToastContext.Provider value={{ success, error, warning, info, loading, dismiss, promise }}>
      {children}
      {/* Portal - bottom-center on mobile, bottom-right on sm+ */}
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-[9999] flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem toast={toast} onDismiss={dismiss} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
